import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, FolderKanban, TrendingUp, Wallet, Sparkles, FileText, Plus, ArrowRight, MessageCircle, Clock, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';
import { formatRp, formatDate, waLink, STAGES } from '@/lib/format';
import { useAuth } from '@/context/AuthContext';
import { useAssistant } from '@/context/AssistantContext';
import { StageBadge } from '@/components/StatusBadge';
import { CardSkeleton } from '@/components/Skeletons';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

function KpiCard({ testid, label, value, icon: Icon, tone = 'primary', onClick }) {
  const tones = {
    primary: 'bg-primary/10 text-primary',
    accent: 'bg-accent/10 text-accent',
    success: 'bg-emerald-100 text-emerald-700',
    warning: 'bg-orange-100 text-orange-700',
  };
  return (
    <button data-testid={testid} onClick={onClick} className="flex flex-col items-start rounded-xl border bg-card p-4 text-left shadow-[var(--shadow-sm)] transition-shadow hover:shadow-[var(--shadow-md)]">
      <span className={cn('mb-3 flex h-9 w-9 items-center justify-center rounded-lg', tones[tone])}><Icon className="h-5 w-5" /></span>
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="mt-0.5 font-display text-xl font-semibold tabular-nums leading-tight">{value}</span>
    </button>
  );
}

export default function Beranda() {
  const { user } = useAuth();
  const { openAssistant } = useAssistant();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'field') { navigate('/tugas', { replace: true }); return; }
    api.get('/dashboard').then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [user, navigate]);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 11) return 'Selamat pagi';
    if (h < 15) return 'Selamat siang';
    if (h < 19) return 'Selamat sore';
    return 'Selamat malam';
  })();

  return (
    <div className="space-y-5">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border bg-card p-5"
        style={{ backgroundImage: 'radial-gradient(900px 400px at 0% 0%, rgba(20,184,166,0.12), transparent 55%), radial-gradient(700px 360px at 100% 0%, rgba(251,146,60,0.10), transparent 60%)' }}>
        <p className="text-sm text-muted-foreground">{greeting},</p>
        <h1 className="font-display text-2xl font-bold">{user?.nama || 'Pemilik'} 👋</h1>
        <p className="mt-1 text-sm text-muted-foreground">Ringkasan bisnis workshop Anda hari ini.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button data-testid="beranda-buat-penawaran" onClick={() => navigate('/penawaran/baru')} className="rounded-xl">
            <Plus className="mr-1.5 h-4 w-4" /> Buat Penawaran Baru
          </Button>
          <Button variant="outline" onClick={() => openAssistant()} className="rounded-xl border-primary/30 text-primary hover:bg-primary/5">
            <Sparkles className="mr-1.5 h-4 w-4" /> Tanya Asisten AI
          </Button>
        </div>
      </div>

      {loading || !data ? <CardSkeleton /> : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <KpiCard testid="dashboard-kpi-lead-baru" label="Lead Baru" value={data.jumlah_lead_baru} icon={Users} tone="accent" onClick={() => navigate('/pelanggan')} />
            <KpiCard testid="dashboard-kpi-proyek-aktif" label="Proyek Aktif" value={data.jumlah_proyek_aktif} icon={FolderKanban} tone="primary" onClick={() => navigate('/proyek')} />
            <KpiCard testid="dashboard-kpi-omzet-bulan-ini" label={`Omzet ${data.bulan_ini || ''}`} value={formatRp(data.omzet_bulan_ini)} icon={TrendingUp} tone="success" onClick={() => navigate('/pembayaran')} />
            <KpiCard testid="dashboard-kpi-total-piutang" label="Total Piutang" value={formatRp(data.total_piutang)} icon={Wallet} tone="warning" onClick={() => navigate('/pembayaran')} />
          </div>

          {/* Pipeline */}
          <div className="rounded-xl border bg-card p-4">
            <h2 className="mb-3 font-display text-sm font-semibold">Proyek per Tahap</h2>
            <div className="flex flex-wrap gap-2">
              {STAGES.map((s) => (
                <button key={s} onClick={() => navigate('/proyek')} className="flex items-center gap-2 rounded-full border bg-background px-3 py-1.5">
                  <StageBadge stage={s} />
                  <span className="font-display text-sm font-semibold tabular-nums">{data.proyek_per_stage?.[s] ?? 0}</span>
                </button>
              ))}
            </div>
          </div>

          {data.low_stock_count > 0 && (
            <button onClick={() => navigate('/inventory')} data-testid="dashboard-low-stock-alert"
              className="flex w-full items-center gap-3 rounded-xl border border-orange-200 bg-orange-50 p-4 text-left">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-orange-700"><AlertTriangle className="h-5 w-5" /></span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-orange-900">{data.low_stock_count} bahan stok menipis</div>
                <div className="truncate text-xs text-orange-700">{(data.low_stock_items || []).map((i) => i.nama).join(', ')}</div>
              </div>
              <ArrowRight className="h-4 w-4 text-orange-600" />
            </button>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            {/* Piutang hari ini */}
            <div className="rounded-xl border bg-card p-4">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-display text-sm font-semibold">Piutang Perlu Ditagih</h2>
                <button onClick={() => navigate('/pembayaran')} className="flex items-center gap-1 text-xs text-primary">Lihat semua <ArrowRight className="h-3 w-3" /></button>
              </div>
              {(!data.daftar_piutang || data.daftar_piutang.length === 0) ? (
                <p className="py-4 text-center text-sm text-muted-foreground">Tidak ada piutang. Mantap! 🎉</p>
              ) : (
                <div className="space-y-2">
                  {data.daftar_piutang.map((p, i) => (
                    <div key={p.proyek_id || `piutang-${i}`} className="flex items-center justify-between rounded-lg bg-orange-50/60 px-3 py-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{p.pelanggan}</div>
                        <div className="truncate text-xs text-muted-foreground">{p.proyek}</div>
                      </div>
                      <div className="text-right text-sm font-semibold text-orange-700">{formatRp(p.sisa)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Lead baru */}
            <div className="rounded-xl border bg-card p-4">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-display text-sm font-semibold">Lead Baru</h2>
                <button onClick={() => navigate('/pelanggan')} className="flex items-center gap-1 text-xs text-primary">Lihat semua <ArrowRight className="h-3 w-3" /></button>
              </div>
              {(!data.lead_baru || data.lead_baru.length === 0) ? (
                <p className="py-4 text-center text-sm text-muted-foreground">Belum ada lead baru.</p>
              ) : (
                <div className="space-y-2">
                  {data.lead_baru.map((l, i) => (
                    <div key={l.id || `${l.nama}-${l.sumber}-${i}`} className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{l.nama}</div>
                        <div className="truncate text-xs text-muted-foreground">Sumber: {l.sumber}</div>
                      </div>
                      {l.whatsapp && (
                        <a href={waLink(l.whatsapp)} target="_blank" rel="noreferrer" className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/10 text-accent hover:bg-accent/20">
                          <MessageCircle className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Activities */}
          <div className="rounded-xl border bg-card p-4">
            <h2 className="mb-3 font-display text-sm font-semibold">Aktivitas Terbaru</h2>
            <div className="space-y-3">
              {(data.recent_activities || []).slice(0, 8).map((a, i) => (
                <div key={a.id || `act-${a.created_at}-${i}`} className="flex gap-3">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><Clock className="h-3.5 w-3.5" /></span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm">{a.entity_name ? <b>{a.entity_name}: </b> : null}{a.note}</div>
                    <div className="text-xs text-muted-foreground">{formatDate(a.created_at)}</div>
                  </div>
                </div>
              ))}
              {(!data.recent_activities || data.recent_activities.length === 0) && (
                <p className="py-2 text-center text-sm text-muted-foreground">Belum ada aktivitas.</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
